import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, KeyRound } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SecurityTab() {
  const { authHeaders, logout } = useAuth();
  const { t } = useLang();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [busy, setBusy] = useState(false);

  const update = (e) => setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 12) {
      toast.error(t("admin.sec.min"));
      return;
    }
    if (form.new_password !== form.confirm_password) {
      toast.error(t("admin.sec.mismatch"));
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: form.current_password, new_password: form.new_password },
        { headers: authHeaders(), withCredentials: true }
      );
      toast.success(t("admin.sec.success"));
      setForm({ current_password: "", new_password: "", confirm_password: "" });
      setTimeout(() => logout(), 1200);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("admin.sec.failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-xl border border-white/10 p-8" data-testid="admin-security-tab">
      <div className="flex items-center gap-3 mb-3">
        <KeyRound size={20} strokeWidth={1.5} className="text-emerald-300" />
        <h2 className="font-display text-2xl font-light tracking-tight">{t("admin.sec.title")}</h2>
      </div>
      <p className="text-sm text-white/60 leading-relaxed mb-8">{t("admin.sec.desc")}</p>
      <form onSubmit={submit} className="space-y-6" autoComplete="off">
        <Field
          label={t("admin.sec.current")}
          name="current_password"
          value={form.current_password}
          onChange={update}
          autoComplete="current-password"
          testid="sec-current-password"
        />
        <Field
          label={t("admin.sec.new")}
          name="new_password"
          value={form.new_password}
          onChange={update}
          autoComplete="new-password"
          hint={t("admin.sec.hint")}
          testid="sec-new-password"
        />
        <Field
          label={t("admin.sec.confirm")}
          name="confirm_password"
          value={form.confirm_password}
          onChange={update}
          autoComplete="new-password"
          testid="sec-confirm-password"
        />
        <button
          type="submit"
          disabled={busy}
          data-testid="sec-submit"
          className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#051630] text-xs tracking-[0.18em] uppercase font-bold hover:bg-transparent hover:text-white border border-white transition-colors disabled:opacity-50"
        >
          {busy && <Loader2 size={14} className="animate-spin" />}
          {busy ? t("admin.sec.saving") : t("admin.sec.submit")}
        </button>
      </form>
    </div>
  );
}

function Field({ label, name, value, onChange, autoComplete, hint, testid }) {
  return (
    <div>
      <label className="block text-[11px] tracking-[0.25em] uppercase text-white/50 mb-2">{label}</label>
      <input
        type="password"
        name={name}
        value={value}
        onChange={onChange}
        required
        minLength={name === "current_password" ? 1 : 12}
        autoComplete={autoComplete}
        data-testid={testid}
        className="w-full bg-transparent border border-white/20 focus:border-white px-4 py-3 text-white text-sm outline-none transition-colors"
      />
      {hint && <p className="mt-2 text-xs text-white/40">{hint}</p>}
    </div>
  );
}
